import { addDays, format, startOfYear } from "date-fns";
import React, { useEffect, useState } from "react";
import { FaCalendar } from "react-icons/fa";
import styled from "styled-components";
import Button from "./Button";
import { Panel } from "./Panel";
import ReactDatePicker from "react-datepicker";

export type DateRange = number | "ytd" | "alltime" | [Date, Date];

export type DateRangeControlProps = {
  value: DateRange;
  onChange: (val: DateRange) => void;
};

export function dateRangeDisplay(value: DateRange) {
  return typeof value === "number"
    ? `Last ${value} days`
    : value === "ytd"
    ? "Year to date"
    : value === "alltime"
    ? "All Time"
    : `${format(value[0], "yyyy-MM-dd")} - ${format(value[1], "yyyy-MM-dd")}`;
}

export function dateRangeValue(value: DateRange): [Date, Date] {
  return typeof value === "number"
    ? [addDays(new Date(), -value), new Date()]
    : value === "ytd"
    ? [startOfYear(new Date()), new Date()]
    : value === "alltime"
    ? [new Date(0), new Date()]
    : value;
}

const dateRangeOptions = [30, 90, "ytd", "alltime"] as const;

const Container = styled.div`
  position: relative;
  width: 250px;
  display: flex;
  justify-content: flex-end;
`;

const List = styled(Panel)`
  position: absolute;
  top: calc(100% + 12px);
  right: 0;
  padding: 3px;
  width: 150px;
`;

const ListItem = styled.div<{ active: boolean }>`
  padding: 3px 12px;
  display: flex;
  align-items: center;
  cursor: pointer;
  color: ${(props) =>
    props.active ? props.theme.colors.body : props.theme.colors.grey3};

  &:before {
    content: "";
    display: block;
    margin-right: 12px;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    border: 1px solid ${(props) => props.theme.colors.grey3};
    border-color: ${(props) =>
      props.active ? props.theme.colors.primary : props.theme.colors.grey3};
    background-color: ${(props) =>
      props.active ? props.theme.colors.primary : "transparent"};
  }
`;

export function DateRangeControl({ value, onChange }: DateRangeControlProps) {
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState<[Date, Date]>([new Date(), new Date()]);

  const isCustom = Array.isArray(value);

  useEffect(() => {
    if (Array.isArray(value)) {
      setCustom(value);
    } else {
      setCustom([null, null]);
    }
  }, [value]);

  const onValueChange = (v: DateRange) => {
    onChange(v);
    setOpen(false);
  };

  const dateRangeChange = (range: [Date, Date]) => {
    setCustom(range);
    if (range.every((x) => x != null)) {
      onValueChange(range);
    }
  };

  return (
    <Container>
      <Button icon={FaCalendar} onClick={() => setOpen(!open)}>
        {dateRangeDisplay(value)}
      </Button>
      {open && (
        <List>
          {dateRangeOptions.map((x) => (
            <ListItem onClick={() => onValueChange(x)} active={value === x}>
              {dateRangeDisplay(x)}
            </ListItem>
          ))}
          <ReactDatePicker
            customInput={<ListItem active={isCustom}>Custom</ListItem>}
            startDate={custom[0]}
            endDate={custom[1]}
            selected={custom[0]}
            selectsRange
            withPortal
            onChange={dateRangeChange}
          />
        </List>
      )}
    </Container>
  );
}
